<!-- No role="dialog"/aria-labelledby here: the ng-bootstrap modal window
     (ngb-modal-window) is already the role="dialog" aria-modal="true" element
     and is given its accessible name via the ariaLabelledBy option passed to
     NgbModal.open (pointing at #dialogTitle below). A second nested role="dialog"
     made JAWS treat this as a modal-within-a-modal and re-read the whole dialog
     body (the intro paragraphs) whenever the window regained focus - e.g. after
     the native file picker closed. -->
<div>
  <div class="modal-content">
    <div class="modal-header">
      <h2 id="dialogTitle" class="modal-title module-title">{{"import.forecast.dialog.title"|translate}}
      </h2>
      <button [disabled]="!_enableCancel || _isUploadingFile" type="button" value="Close" class="close" aria-label="Close"
              title="{{'dialog.box.close.tooltip'|translate}}"
              ngbAutofocus
              (click)="dismiss()">
        <span class="icon-Close" aria-hidden="true"></span>
      </button>
    </div>
    <div class="modal-body">
      <wfm-plan-loading-overlay *ngIf="_isLoading"></wfm-plan-loading-overlay>
      <p-toast position="bottom-right"></p-toast>
      <!-- Upload errors are announced through a SINGLE channel: a role="alert"
           region (#msgErrorValidation for plain validation errors,
           #chkboxErrorMessage for the file-exists/overwrite case). role="alert"
           already implies aria-live="assertive"/aria-atomic, so those are not
           declared explicitly. Deliberately NO aria-describedby / aria-invalid on
           the Choose File button and NO focus move onto the regions: each of
           those is a SECOND (or third) place the same error text is read - when
           the native file picker returns focus to the Choose File button JAWS
           re-reads the button plus any aria-describedby/aria-invalid, and
           focusing a live region re-reads it - which is the "announced multiple
           times" defect. The live region alone announces the error exactly once.
           See renderErrorForAnnouncement() in the component. -->
      <div class="col-12">
        <div class="row">
          <div class='col-12 mt-3'>
            <p>
              {{"import.forecast.dialog.paragraph.template"| translate}}
              <button id="btn-download-template" type="button" class="btn btn-link"
                      [attr.aria-label]="'import.forecast.dialog.template.link.aria' | translate"
                      (click)="download()"
                      [disabled]="_isUploadingFile">{{"import.forecast.dialog.template.link"| translate}}</button>.
            </p>
          </div>
        </div>
        <div class="row">
          <div class='col-12 mt-3'>
            <p [innerHTML]="'import.forecast.dialog.paragraph.important' | translate"></p>
          </div>
        </div>
        <div class="row">
          <div class='col-12 mt-3'>
            <!-- EXPERIMENTAL (ESP-18588): while the native OS file picker is open
                 (_silenceChooseFileName) the button's accessible name is blanked -
                 the visible label span is aria-hidden and the title is removed.
                 When the picker closes, Chrome regains focus and JAWS speaks a
                 "window-activation" snapshot of the foreground window PLUS the
                 control that still has focus (this Choose File button) BEFORE our
                 (change)/validation handler ever runs, so no web focus move can
                 stop it. Blanking the name means that snapshot has no control name
                 to read; the name is restored shortly AFTER focus returns (see
                 onChooseFileClick). The label stays visually intact for sighted
                 users (aria-hidden hides it from AT only). -->
            <button class="btn btn-secondary btn-sm" (click)="onChooseFileClick(fileInput)"
                    [attr.title]="_silenceChooseFileName ? null : ('import.forecast.dialog.file.choose' | translate)"
                    [disabled]="_isUploadingFile">
              <span [attr.aria-hidden]="_silenceChooseFileName ? 'true' : null">{{'import.forecast.dialog.file.choose' | translate }}</span>
              <input #fileInput type="file" id="uploadFile" name="uploadFile"
                     (change)="onFileChange($event)" accept=".csv" style="display:none;" />
            </button>
            &nbsp;&nbsp;
            <span>{{_messageSelectedFile}}</span>
            <!-- Sole SR + visual channel for a plain validation error: a
                 role="alert" region. role="alert" already implies
                 aria-live="assertive" and aria-atomic="true", so those are NOT
                 declared explicitly - declaring them too can make some screen
                 readers register the region twice and announce the error twice.
                 It is programmatically associated with the Choose File button
                 above via aria-describedby. It announces itself exactly ONCE when
                 it appears; focus is NOT moved onto it (focusing a live region
                 makes the screen reader read it a second time). Toggled with
                 [hidden] rather than *ngIf so it stays in the DOM (a live region
                 must exist before it needs to speak) and the layout stays
                 stable. -->
            <div #errorAlert [hidden]="!_showError || _showCheckBox" id="msgErrorValidation"
                 role="alert"
                 class="error-message error-message-label" [innerHTML]="_errorMessage">
            </div>
            <!-- File-already-exists case: an actionable overwrite checkbox. The
                 container is a role="alert" region (implicit assertive/atomic) so
                 the instruction is announced exactly ONCE as soon as it appears;
                 focus is NOT moved into it (that would read the content again).
                 The checkbox accessible name comes from aria-label so the
                 visible label text can stay aria-hidden and not be double-read. -->
            <div *ngIf="_showError && _showCheckBox" role="alert" class="checkbox checkbox-inline error-message" id="chkboxErrorMessage">
              <!-- The role="alert" region announces the instruction exactly once
                   via the checkbox's accessible name (aria-label). The visible
                   label text is aria-hidden so it is NOT read a second time
                   alongside that name. Do NOT add another copy of the message
                   (e.g. an sr-only span) here - it would be announced twice. -->
              <input #errorChkbox type="checkbox" id="errorChkbox" value="errorChkbox" [(ngModel)]="isConfirmed" (click)="enableUploadButton($event);">
              <label for="errorChkbox" aria-label="{{_chkboxErrorMessage}}" title="{{_chkboxErrorMessage}}"><span aria-hidden="true">{{_chkboxErrorMessage}}</span></label>
            </div>
          </div>
        </div>
        <div class="row" *ngIf="_isUploadingFile">
          <div class='col-12 mt-3'>
            <span class="b-p-textlabel">{{'import.forecast.dialog.file.upload.progress.label' | translate}}</span>
            <div class="b-p-progress">
              <div id="b-progress-bar" [style.width]="_percentageLoaded + '%'" class="b-progress-bar" role="progressbar"></div>
            </div>
            <span id="b-p-label" class="b-p-label">{{_percentageLoaded}}%</span>
          </div>
        </div>
      </div>
    </div>
    <div class="modal-footer">
      <div class="modal-buttons">
        <breeze-button buttonText="{{'import.forecast.dialog.cancel.button'|translate}}" id="cancel-btn"
                       [isSecondary]="true" [isDisabled]="!_enableCancel || _isUploadingFile" (clicked)="dismiss()">
        </breeze-button>
        <breeze-button buttonText="{{'import.forecast.dialog.upload.button'|translate}}" id="accept-btn"
                       [isPrimary]="true" [isDisabled]="!_enableAccept || _isUploadingFile" (clicked)="uploadFile()"
                       placement="top">
        </breeze-button>
      </div>
    </div>
  </div>
</div>
